import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, User, LogOut, Sparkles, ChevronDown, Bot } from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { label: 'Daily Drop', href: '/daily-drop' },
  { label: 'Deep Dives', href: '/deep-dives' },
  { label: 'Video Vault', href: '/video-vault' },
  { label: 'Knowledge', href: '/knowledge' },
  { label: 'Products', href: '/products' },
  { label: 'Quiz', href: '/quiz' },
  { label: 'Leaderboard', href: '/leaderboard' },
];

export default function Navbar() {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (href) => location.pathname === href;

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-serif text-xl font-semibold tracking-tight">SkinUse</span>
            <span className="hidden sm:inline-block w-1 h-1 bg-gold rounded-full"></span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'text-gold'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* AI Advisor Button - visible for everyone */}
            <Link to="/ai-advisor">
              <Button variant="ghost" size="sm" className="hidden sm:flex gap-2 text-gold hover:text-gold/80" data-testid="nav-ai-advisor">
                <Bot className="w-4 h-4" />
                <span className="text-sm">AI Advisor</span>
              </Button>
            </Link>

            {isAuthenticated ? (
              <>
                {/* SkinPoints */}
                <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gold/10 rounded-sm" data-testid="nav-skinpoints">
                  <Sparkles className="w-4 h-4 text-gold" />
                  <span className="text-sm font-semibold text-gold">{user?.skin_points || 0}</span>
                </div>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 px-3" data-testid="nav-user-menu">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        {user?.picture ? (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <User className="w-4 h-4 text-primary-foreground" />
                        )}
                      </div>
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" data-testid="menu-dashboard">Dashboard</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" data-testid="menu-profile">Profile</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" data-testid="menu-settings">Appearance</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-sm" data-testid="nav-login">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary text-sm" data-testid="nav-register">Get Started</Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="nav-mobile-menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <div className="flex flex-col gap-6 mt-8">
                  <Link to={isAuthenticated ? '/dashboard' : '/'} className="font-serif text-2xl" onClick={() => setMobileOpen(false)}>
                    SkinUse
                  </Link>
                  <div className="flex flex-col gap-2">
                    {navItems.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={`px-4 py-3 text-base font-medium rounded-sm transition-colors ${
                          isActive(item.href)
                            ? 'bg-gold/10 text-gold'
                            : 'hover:bg-muted'
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                  {isAuthenticated && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-gold/10 rounded-sm">
                      <Sparkles className="w-5 h-5 text-gold" />
                      <span className="font-semibold text-gold">{user?.skin_points || 0} SkinPoints</span>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
